import {COMPASS_APPS, compassApp} from '../../Util/compassApp';

export const SHARE_MAX_EXPIRY_DAYS = 30;

const SHARED_ROUTE_SEGMENT = 'shared';

// A link opens in the sharer's own application, under its prefix on any host:
// `/parents/shared/<token>` (COM-452).
export const buildShareUrl = (token) => {
  if (typeof token !== 'string' || !token.trim()) {
    console.error('buildShareUrl received an invalid token.', {token});
    return null;
  }

  const encodedToken = encodeURIComponent(token.trim());
  const app = compassApp();
  return `${window.location.origin}${app ? `/${app}` : ''}/${SHARED_ROUTE_SEGMENT}/${encodedToken}`;
};

// The token a share link's path names, in the form links are made in now
// (`/parents/shared/<token>`) or the unprefixed one they were made in before
// (`/shared/<token>`), which every application still opens. Empty for any other path.
export const shareTokenFromPath = (pathname) => {
  const pathParts = pathname.split('/').filter(Boolean);
  const [segment, token] = COMPASS_APPS.includes(pathParts[0]) ? pathParts.slice(1) : pathParts;

  return segment === SHARED_ROUTE_SEGMENT ? token ?? '' : '';
};

/**
 * The variant of the polite Alert a share message is announced in. A failure is
 * never one of them: it is the error Alert the dialog and the manager insert
 * beside it, so the polite region keeps its status role.
 */
export const statusVariant = (severity) =>
  severity === 'warning' || severity === 'info' ? severity : 'success';

export const formatShareDate = (value, locale, fallback = 'N/A') => {
  if (!value) {
    return fallback;
  }

  const browserLocale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : undefined;
  const formattedLocale = locale ?? browserLocale;
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return new Intl.DateTimeFormat(formattedLocale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

export const copyTextToClipboard = async (text) => {
  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.warn('Clipboard API copy failed. Falling back to legacy copy method.', error);
    }
  }

  const textarea = document.createElement('textarea');
  let isCopied = false;

  try {
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    // Keep the legacy path for older browsers that do not support navigator.clipboard.
    isCopied = document.execCommand('copy');
  } catch (error) {
    console.error('Legacy clipboard copy failed.', error);
  } finally {
    if (textarea.parentNode) {
      textarea.parentNode.removeChild(textarea);
    }
  }

  return isCopied;
};

const sanitizeEmailField = (value, maxLength = 500) => {
  if (typeof value !== 'string') {
    return '';
  }

  const sanitized = value
    .replace(/[\r\n\0]/g, ' ')
    .replace(/\b(to|cc|bcc|subject)\s*:/gi, '$1 ')
    .trim();

  if (!maxLength || sanitized.length <= maxLength) {
    return sanitized;
  }

  return sanitized.slice(0, maxLength);
};

export const buildShareEmailUrl = ({resourceName, url, message}) => {
  const safeResourceName = sanitizeEmailField(resourceName, 120) || 'Compass resource';
  const safeMessage = sanitizeEmailField(message, 1000);
  const safeUrl = typeof url === 'string' ? sanitizeEmailField(url.trim(), 2000) : '';
  const subject = `Compass shared resource: ${safeResourceName}`;
  const bodyLines = [];

  if (safeMessage) {
    bodyLines.push(safeMessage);
    bodyLines.push('');
  }

  bodyLines.push(`Here is a Compass resource I wanted to share with you:`);
  if (safeUrl) {
    bodyLines.push(safeUrl);
  }

  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
    bodyLines.join('\n')
  )}`;
};
